import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subject, takeUntil } from 'rxjs';
import { ToasterService, ToasterMessage } from '../../services/toaster.service';

@Component({
  selector: 'app-toaster',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './toaster.html',
  styleUrl: './toaster.scss',
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class ToasterComponent implements OnInit, OnDestroy {
  private toasterService = inject(ToasterService);
  private cdr = inject(ChangeDetectorRef);

  messages: ToasterMessage[] = [];
  private destroy$ = new Subject<void>();
  private timeouts: Map<string, number> = new Map();

  ngOnInit(): void {
    this.toasterService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(messages => {
        this.messages = messages;
        this.setupAutoDismiss();
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    // Clear all timeouts
    this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.timeouts.clear();
    
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupAutoDismiss(): void {
    // Clear existing timeouts for messages that are no longer present
    const currentMessageIds = this.messages.map(m => m.id);
    this.timeouts.forEach((timeoutId, messageId) => {
      if (!currentMessageIds.includes(messageId)) {
        clearTimeout(timeoutId);
        this.timeouts.delete(messageId);
      }
    });

    // Set up auto-dismiss for new messages
    this.messages.forEach(message => {
      if (message.duration && message.duration > 0 && !this.timeouts.has(message.id)) {
        const timeoutId = window.setTimeout(() => {
          this.dismissMessage(message.id);
          this.timeouts.delete(message.id);
        }, message.duration);
        this.timeouts.set(message.id, timeoutId);
      }
    });
  }

  dismissMessage(id: string): void {
    // Clear timeout if it exists
    if (this.timeouts.has(id)) {
      clearTimeout(this.timeouts.get(id)!);
      this.timeouts.delete(id);
    }
    this.toasterService.dismiss(id);
    this.cdr.detectChanges();
  }

  getMessageIcon(type: ToasterMessage['type']): string {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return 'ℹ';
    }
  }

  getMessageClass(type: ToasterMessage['type']): string {
    return `toast-${type}`;
  }

  getPositionClass(position: ToasterMessage['position']): string {
    return `position-${position}`;
  }

  trackByMessageId(index: number, message: ToasterMessage): string {
    return message.id;
  }
}
